import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosResponse, isAxiosError } from 'axios';
import { useTranslations } from 'next-intl';
import { useRouter } from 'next/navigation';
import { Dispatch, SetStateAction, useState } from 'react';

import { postPrizeEntryApi } from '@flipster/apis/v1/campaign';
import { GetPrizeEntryResponse, PrizeEntryItem } from '@flipster/apis/v1/campaign/types';
import { useAuth } from '@flipster/features/src/shared/auth/state';
import { useOverlayItem } from '@flipster/overlay';
import { Modal } from '@flipster/react/design-system/modal';
import { snackbar } from '@flipster/react/design-system/snackbar/index';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import { css } from '@flipster/styled-system/css';
import { center, flex } from '@flipster/styled-system/patterns';
import { ServerError } from '@flipster/utils/constants/server-error';
import { numberFormatter } from '@flipster/utils/num';

const validateEntries = (entries: number, availableTickets: number) => {
  if (entries > availableTickets) {
    throw new Error('You don’t have enough entries.');
  }
};

interface SelectEntryButtonsProps {
  availableTickets: number;
  entries: number;
  setEntries: Dispatch<SetStateAction<number>>;
  setError: Dispatch<SetStateAction<string | null>>;
}

const SelectEntryButtons = ({ availableTickets, entries, setEntries, setError }: SelectEntryButtonsProps) => {
  const selectEntryButtonItems = [
    { label: '+1', value: 1 },
    { label: '+5', value: 5 },
    { label: '+10', value: 10 },
    { label: 'All', value: availableTickets },
  ];

  const addEntries = (value: number) => {
    try {
      const next = value === availableTickets ? availableTickets : entries + value;
      setEntries(next);
      validateEntries(next, availableTickets);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : null);
    }
  };

  return (
    <div className={flex({ gap: 6, mt: 40, w: '100%' })}>
      {selectEntryButtonItems.map((item) => {
        return (
          <button
            type='button'
            key={item.value}
            onClick={() => addEntries(item.value)}
            className={center({
              flex: 1,
              p: 10,
              rounded: 4,
              bgColor: 'background.default.level-2',
            })}
          >
            <span
              className={css({
                textStyle: 'Text/S/Strong',
                color: 'content.default.level-2',
                textAlign: 'center',
              })}
            >
              {item.label}
            </span>
          </button>
        );
      })}
    </div>
  );
};

interface SubmitPrizeEntryModalProps {
  item: PrizeEntryItem;
  availableTickets: number;
  slug: string;
}

const SubmitPrizeEntryModal = ({ item, availableTickets, slug }: SubmitPrizeEntryModalProps) => {
  const [entries, setEntries] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { onClose } = useOverlayItem();
  const queryClient = useQueryClient();
  const { isCookieLoggedIn } = useAuth();
  const t = useTranslations();
  const router = useRouter();
  const { mutateAsync: submitPrizeEntry } = useMutation({
    mutationKey: ['postPrizeEntryApi'],
    mutationFn: postPrizeEntryApi,
    onSuccess: ({ data }) => {
      queryClient.setQueryData(
        ['getPrizeEntryApi', slug, isCookieLoggedIn],
        (old: AxiosResponse<GetPrizeEntryResponse>) => ({
          ...old,
          data: {
            ...old.data,
            myTicket: data.myTicket,
            items: old.data.items.map((item) =>
              item.id === data.item.id
                ? {
                    ...item,
                    totalEntries: data.item.totalEntries,
                    yourEntries: data.item.yourEntries,
                  }
                : item,
            ),
          },
        }),
      );
      snackbar.success({ message: `${entries} draw entries updated for ${item.name}` });
    },
    onError: (error) => {
      if (!isAxiosError(error)) return;
      switch (error.response?.status) {
        case 400:
          snackbar.error({ message: 'Your tickets may have been used.\nPlease refresh and try again.' });
          break;
        case 401:
          snackbar.error({ message: t('Common.r250918.Please_log_in') });
          router.push('/login');
          break;
        default:
          snackbar.error({ message: ServerError['api.server.error'] });
          break;
      }
      queryClient.refetchQueries({ queryKey: ['getPrizeEntryApi', slug, isCookieLoggedIn] });
    },
  });

  // 처음 응모하는 경우 Submit, 이후 추가하는 경우 Add
  const title = (item.yourEntries ?? 0) > 0 ? 'Add entry amount' : 'Submit entry amount';

  const changeEntriesInput = (value: string) => {
    const next = value === '' ? 0 : Number(value);
    setEntries(next);
    try {
      validateEntries(next, availableTickets);
      setError(null);
    } catch (error) {
      setError(error instanceof Error ? error.message : null);
    }
  };

  const handleSubmit = () => {
    validateEntries(entries, availableTickets);
    if (error) return;
    submitPrizeEntry({ eventId: slug, itemId: item.id, count: entries });
    onClose();
  };

  return (
    <Modal.Root onClose={onClose}>
      <Modal.Header>
        <Modal.Title label={title} />
      </Modal.Header>
      <Modal.Content
        css={{
          tabletDown: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flex: 1,
            flexDirection: 'column',
          },
        }}
      >
        <div className={center({ w: '100%', gap: 4, tablet: { mt: 40 } })}>
          <FormattedNumberInput
            value={entries === 0 ? '' : entries.toString()}
            onChange={changeEntriesInput}
            placeholder='0'
            inputMode='numeric'
            className={css({
              textAlign: 'right',
              textStyle: 'Title/M',
              color: 'content.default.level-4',
              caretColor: 'content.accent.info',
            })}
            style={{
              width: `${Math.max(1, entries.toString().length ?? 0)}ch`,
            }}
          />
          <span className={css({ textStyle: 'Title/M', color: 'content.default.level-4' })}>Entries</span>
        </div>
        <p className={css({ textStyle: 'Text/M/Regular', textAlign: 'center', mt: 12 })}>
          {error ? (
            <span className={css({ color: 'content.state.negative' })}>{error}</span>
          ) : (
            <span className={css({ color: 'content.default.level-2' })}>
              Your available entries: {numberFormatter(availableTickets)}
            </span>
          )}
        </p>
        <SelectEntryButtons
          availableTickets={availableTickets}
          entries={entries}
          setEntries={setEntries}
          setError={setError}
        />
      </Modal.Content>
      <Modal.Footer>
        <Modal.Button label='Confirm' onClick={handleSubmit} disabled={entries === 0 || Boolean(error)} />
      </Modal.Footer>
    </Modal.Root>
  );
};

export default SubmitPrizeEntryModal;
